import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Award, PlayCircle, Plus, Trash2, GraduationCap, CalendarDays, BookMarked, ReceiptText, ShieldCheck, Camera, Check, NotebookPen } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileToResizedDataURL } from "@/lib/image";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { EditProfileDialog, catLabel } from "@/components/profile/EditProfileDialog";
import { ChangePasswordDialog } from "@/components/profile/ChangePasswordDialog";
import { EmailDialog } from "@/components/profile/EmailDialog";
import { ContactDialog } from "@/components/profile/ContactDialog";
import { AddressesDialog } from "@/components/profile/AddressesDialog";
import { MyEventsDialog } from "@/components/profile/MyEventsDialog";
import { TradingJournal } from "@/components/journal/TradingJournal";

function DashCard({ icon: Icon, title, subtitle, badge, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group text-left p-5 rounded-lg bg-navy grain border border-white/10 hover:border-gold/40 transition-all">
      <div className="flex items-center justify-between">
        <span className="h-11 w-11 rounded-lg bg-gold/15 text-gold flex items-center justify-center"><Icon className="h-5 w-5" /></span>
        {badge}
      </div>
      <p className="font-display font-semibold text-white mt-4">{title}</p>
      <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
    </button>
  );
}

function PlaceholderCard({ icon, title, subtitle, dialogTitle, dialogDesc, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <DashCard icon={icon} title={title} subtitle={subtitle} onClick={() => setOpen(true)} testid={testid} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{dialogTitle}</DialogTitle></DialogHeader>
          <div className="text-center py-8">
            {(() => { const I = icon; return <I className="h-10 w-10 text-slate-300 mx-auto" />; })()}
            <p className="text-slate-500 mt-3">{dialogDesc}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addrCount, setAddrCount] = useState(0);
  const [myEvents, setMyEvents] = useState([]);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [journalCount, setJournalCount] = useState(0);
  const fileRef = useRef(null);

  const load = () => api.get("/my/enrollments").then((r) => setEnrollments(r.data)).finally(() => setLoading(false));
  const loadAddresses = () => api.get("/me/addresses").then((r) => setAddrCount(r.data.length)).catch(() => {});
  const loadMyEvents = () => api.get("/my/events").then((r) => setMyEvents(r.data)).catch(() => {});
  const loadOrders = () => api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); loadAddresses(); loadMyEvents(); loadOrders(); }, []);

  const completed = enrollments.filter((e) => e.progress === 100);
  const active = enrollments.filter((e) => e.progress < 100);
  const recordings = myEvents.filter((e) => e.event_type === "recorded");
  const liveEvents = myEvents.filter((e) => e.event_type !== "recorded");
  const upcomingEvents = liveEvents.filter((e) => { const d = new Date(e.starts_at); return isNaN(d.getTime()) || d.getTime() >= Date.now(); });

  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      logout();
      toast.success("Your account has been permanently deleted.");
      navigate("/");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataURL(f, { max: 256, square: true, type: "image/jpeg", quality: 0.85 });
      await api.post("/me/avatar", { image: dataUrl });
      await refreshUser();
      toast.success("Profile photo updated");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removePhoto = async () => {
    try { await api.delete("/me/avatar"); await refreshUser(); toast.success("Photo removed"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const initials = (user?.name || user?.email || "U").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "";
  const scrollToLearning = () => document.getElementById("my-learning")?.scrollIntoView({ behavior: "smooth" });

  const checklist = [
    { label: "Name", done: !!user?.name },
    { label: "Email", done: !!user?.email },
    { label: "Mobile", done: !!user?.mobile },
    { label: "Category", done: !!user?.category && user?.category !== "admin" },
    { label: "Photo", done: !!user?.picture },
    { label: "Address", done: addrCount > 0 },
  ];
  const doneCount = checklist.filter((c) => c.done).length;
  const completeness = Math.round((doneCount / checklist.length) * 100);

  return (
    <div data-testid="dashboard-page">
      {/* Profile header */}
      <section className="bg-navy grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="eyebrow text-gold">My Profile</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mt-4">
            <div className="flex items-center gap-5">
              <div className="relative group">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="avatar-file-input" />
                <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
                  className="relative h-20 w-20 rounded-full overflow-hidden ring-2 ring-gold/40 flex items-center justify-center bg-gold/20 text-gold font-display text-2xl font-bold">
                  {user?.picture ? <img src={user.picture} alt={user?.name} className="h-full w-full object-cover" /> : <span data-testid="profile-avatar">{initials}</span>}
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="h-6 w-6 text-white" />
                  </span>
                </button>
                {uploading && <span className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center text-[10px] text-white">Uploading…</span>}
              </div>
              <div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{user?.name || "Welcome"}</h1>
                <p className="text-slate-400 text-sm mt-0.5">{user?.email || (user?.mobile ? `+91 ${user.mobile}` : "")}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[11px] uppercase tracking-wide bg-white/10 text-slate-200 rounded-full px-2.5 py-0.5">{catLabel(user?.category)}</span>
                  {memberSince && <span className="text-[11px] text-slate-500">Member since {memberSince}</span>}
                  {user?.picture && <button onClick={removePhoto} data-testid="remove-photo-btn" className="text-[11px] text-slate-500 hover:text-red-400 transition-colors">Remove photo</button>}
                </div>
              </div>
            </div>

            {/* Completeness meter */}
            <div className="sm:ml-auto w-full sm:w-72 bg-white/5 border border-white/10 rounded-lg p-4" data-testid="profile-completeness">
              <div className="flex justify-between text-xs text-slate-300 mb-2"><span>Profile completeness</span><span className="text-gold font-semibold">{completeness}%</span></div>
              <Progress value={completeness} className="h-2" />
              <div className="flex flex-wrap gap-1.5 mt-3">
                {checklist.map((c) => (
                  <span key={c.label} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${c.done ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-400"}`}>
                    {c.done && <Check className="h-2.5 w-2.5" />}{c.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* My Account */}
        <div data-testid="my-account-section">
          <h2 className="font-display text-2xl font-bold text-navy mb-5">My Account</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <EditProfileDialog user={user} onDone={refreshUser} />
            <ChangePasswordDialog user={user} onDone={refreshUser} />
            <EmailDialog user={user} />
            <ContactDialog user={user} onDone={refreshUser} />
            <div className="sm:col-span-2"><AddressesDialog /></div>
          </div>
        </div>

        {/* Dashboard */}
        <div data-testid="dashboard-section">
          <h2 className="font-display text-2xl font-bold text-navy mb-5">Dashboard</h2>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <DashCard icon={GraduationCap} title="My Learning" subtitle={`${enrollments.length} enrolled · ${active.length} in progress`}
              badge={<span className="font-display text-xl font-bold text-gold">{enrollments.length}</span>} onClick={scrollToLearning} testid="dash-learning" />
            <DashCard icon={CalendarDays} title="My Recordings & Events" subtitle={`${recordings.length ? recordings.length + " recording" + (recordings.length > 1 ? "s" : "") : "No recordings"} · ${upcomingEvents.length} upcoming`}
              badge={<span className="font-display text-xl font-bold text-gold">{myEvents.length}</span>} onClick={() => setEventsOpen(true)} testid="dash-webinars" />
            <DashCard icon={NotebookPen} title="Trading Journal" subtitle={journalCount ? `${journalCount} entries` : "Log your trades"}
              badge={<span className="font-display text-xl font-bold text-gold">{journalCount}</span>} onClick={() => document.getElementById("trading-journal")?.scrollIntoView({ behavior: "smooth" })} testid="dash-journal" />
            <PlaceholderCard icon={BookMarked} title="My Books" subtitle="No books yet"
              dialogTitle="My Books" dialogDesc="Your purchased books will appear here. Our authored titles are coming soon." testid="dash-books" />
            <DashCard icon={ReceiptText} title="Order History" subtitle={orders.length ? `${orders.length} order${orders.length > 1 ? "s" : ""}` : "No orders yet"}
              badge={<span className="font-display text-xl font-bold text-gold">{orders.length}</span>} onClick={() => setOrdersOpen(true)} testid="dash-orders" />
          </div>
          <MyEventsDialog open={eventsOpen} onOpenChange={setEventsOpen} events={myEvents} onChanged={loadMyEvents} />
          <Dialog open={ordersOpen} onOpenChange={setOrdersOpen}>
            <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="orders-dialog">
              <DialogHeader><DialogTitle>Order History</DialogTitle></DialogHeader>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <ReceiptText className="h-10 w-10 text-slate-300 mx-auto" />
                  <p className="text-slate-500 mt-3">No orders yet. Your courses, recordings and event bookings will appear here.</p>
                </div>
              ) : (
                <div className="divide-y divide-border" data-testid="orders-list">
                  {orders.map((o, i) => {
                    const d = new Date(o.date);
                    const dateStr = isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                    const badgeCls = o.status === "paid" ? "bg-emerald-100 text-emerald-800" : o.status === "bonus" ? "bg-gold/20 text-gold" : "bg-slate-100 text-slate-500";
                    return (
                      <div key={o.id || i} className="py-3 flex items-center justify-between gap-3" data-testid={`order-row-${i}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-navy truncate">{o.title}</p>
                          <p className="text-xs text-slate-400 mt-0.5 capitalize">{o.kind} · {dateStr}{o.reference ? ` · ${o.reference}` : ""}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-navy">{o.amount > 0 ? `₹${o.amount.toLocaleString("en-IN")}` : "Free"}</p>
                          <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${badgeCls}`}>{o.status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* My Learning content */}
        <div id="my-learning" data-testid="my-learning-content">
          <h2 className="font-display text-2xl font-bold text-navy mb-5">My Learning</h2>
          {loading ? (
            <p className="text-slate-400">Loading your learning…</p>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="no-enrollments">
              <BookOpen className="h-10 w-10 text-slate-300 mx-auto" />
              <h3 className="font-display text-xl font-semibold text-navy mt-4">No courses yet</h3>
              <p className="text-slate-500 mt-2">Explore courses and start your structured learning journey.</p>
              <Link to="/courses" className="inline-flex items-center gap-2 mt-6 h-11 px-6 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors" data-testid="browse-courses">
                <Plus className="h-4 w-4" /> Browse Courses
              </Link>
            </div>
          ) : (
            <div className="space-y-10">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {enrollments.map((e) => (
                  <div key={e.course.slug} className="bg-white border border-border rounded-lg overflow-hidden" data-testid={`enrolled-${e.course.slug}`}>
                    {e.course.image ? (
                      <img src={e.course.image} alt={e.course.title} className="h-32 w-full object-cover" />
                    ) : (
                      <div className="h-32 w-full bg-navy grain flex items-center justify-center"><span className="font-display text-gold font-bold">KRV Prime</span></div>
                    )}
                    <div className="p-5">
                      <p className="eyebrow text-slate-400">{e.course.category}</p>
                      <h3 className="font-display text-lg font-semibold text-navy mt-1">{e.course.title}</h3>
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-slate-500 mb-1.5"><span>Progress</span><span>{e.progress}%</span></div>
                        <Progress value={e.progress} className="h-2" />
                      </div>
                      {e.progress === 100 ? (
                        <div className="flex items-center justify-between mt-4">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600"><Award className="h-4 w-4" />Completed</span>
                          <Link to={`/courses/${e.course.slug}/learn`} data-testid={`revisit-${e.course.slug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold transition-colors"><PlayCircle className="h-4 w-4" /> Revisit</Link>
                        </div>
                      ) : (
                        <Link to={`/courses/${e.course.slug}/learn`} data-testid={`continue-${e.course.slug}`} className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-navy hover:text-gold transition-colors">
                          <PlayCircle className="h-4 w-4" /> {e.progress > 0 ? "Continue learning" : "Start course"}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {completed.length > 0 && (
                <div>
                  <h3 className="font-display text-xl font-bold text-navy mb-4">Certificates</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {completed.map((e) => (
                      <div key={e.course.slug} className="border border-gold/40 bg-gold/5 rounded-lg p-6 flex items-center gap-4" data-testid={`certificate-${e.course.slug}`}>
                        <Award className="h-10 w-10 text-gold" />
                        <div>
                          <h4 className="font-display font-semibold text-navy">{e.course.title}</h4>
                          <p className="text-xs text-slate-500 mt-0.5">Certificate ID: KRV-{e.course.slug.slice(0, 6).toUpperCase()}-{user?.id?.slice(0, 4).toUpperCase()}</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">Educational certificate — not a professional license.</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trading Journal */}
        <TradingJournal onLoaded={setJournalCount} />

        {/* Danger zone */}
        <div className="border border-red-200 rounded-lg p-5 bg-red-50/40" data-testid="danger-zone">
          <h3 className="font-display font-semibold text-navy flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-red-500" /> Delete Account</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Permanently delete your account and all associated data — profile, name, email, mobile number, photo, saved addresses, bookmarks and learning activity. This action cannot be undone.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-account-btn" className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"><Trash2 className="h-4 w-4" /> Delete Account</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                <AlertDialogDescription>This permanently deletes your KRV Prime account and all associated data. This action cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} data-testid="delete-confirm" className="bg-red-600 hover:bg-red-700">Yes, delete my account</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
